import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Package } from 'lucide-react';
import { Button } from './ui/button';
import { productImage } from '../utils/imageUtils';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable search button + dropdown overlay used on Products / Category / Subcategory pages.
 *
 * Props:
 *  - language: 'sv' | 'en'
 *  - initialQuery?: pre-fill input on open (e.g. current ?search= param)
 *  - onSubmit?: (query: string) => void — when present, called instead of default navigate.
 *               Use this on the Products page to sync URL search params in place.
 */
const ProductSearchButton = ({ language = 'sv', initialQuery = '', onSubmit }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced autocomplete
  useEffect(() => {
    if (!open) return;
    const q = searchInput.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search/products?q=${encodeURIComponent(q)}&limit=6`);
        setSuggestions(res.data?.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(handle);
  }, [searchInput, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    setOpen(false);
    if (onSubmit) {
      onSubmit(q);
    } else if (q) {
      navigate(`/produkter?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/produkter');
    }
  };

  return (
    <div className="relative">
      {/* Speech bubble — visible until search is opened (desktop only; covered filters on mobile) */}
      {!open && (
        <div
          data-testid="search-tooltip-bubble"
          className="hidden md:block absolute right-0 -top-12 z-30 pointer-events-none"
        >
          <div className="relative bg-[#321a27] text-white text-xs font-semibold px-3 py-2 shadow-lg whitespace-nowrap">
            {language === 'sv' ? 'Det ska vara enkelt att hitta rätt' : 'It should be easy to find the right one'}
            {/* Tail pointing down to the button */}
            <span className="absolute right-6 -bottom-1.5 w-3 h-3 bg-[#321a27] rotate-45"></span>
          </div>
        </div>
      )}

      <Button
        variant="outline"
        onClick={() => {
          setSearchInput(initialQuery);
          setOpen((prev) => !prev);
        }}
        data-testid="products-search-button"
        className={`border-stone-300 hover:bg-stone-100 rounded-none flex items-center gap-2 ${
          initialQuery ? 'bg-[#f5f0f2] border-[#d4c4cc] text-[#321a27]' : 'text-stone-700'
        }`}
      >
        <Search className="w-4 h-4" />
        {language === 'sv' ? 'Sök' : 'Search'}
        {initialQuery && <span className="text-xs">("{initialQuery}")</span>}
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="products-search-overlay"
            className="absolute right-0 top-full mt-2 w-[440px] max-w-[calc(100vw-2rem)] bg-white border border-stone-200 shadow-xl z-50 p-4"
          >
            <form onSubmit={handleSubmit}>
              <label className="block text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
                {language === 'sv' ? 'Sök bland produkter' : 'Search products'}
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    autoFocus
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={language === 'sv' ? 'Namn, beskrivning, artikel...' : 'Name, description, item...'}
                    className="pl-9 rounded-none border-stone-300 focus:border-[#321a27]"
                    data-testid="products-search-input"
                  />
                </div>
                <Button type="submit" data-testid="products-search-submit" className="rounded-none bg-[#321a27] text-white hover:bg-[#4a2d3d]">
                  {language === 'sv' ? 'Sök' : 'Search'}
                </Button>
              </div>

              {searchInput.trim().length >= 2 && (
                <div className="mt-3 border border-stone-200 bg-white max-h-[360px] overflow-y-auto" data-testid="search-suggestions">
                  {loading && suggestions.length === 0 ? (
                    <div className="p-3 text-xs text-stone-400 text-center">
                      {language === 'sv' ? 'Söker...' : 'Searching...'}
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="p-4 text-xs text-stone-400 text-center">
                      {language === 'sv' ? 'Inga förslag' : 'No suggestions'}
                    </div>
                  ) : (
                    <ul className="divide-y divide-stone-100">
                      {suggestions.map((s, idx) => {
                        const name = language === 'sv' ? s.name_sv : s.name_en;
                        const imgSrc = s.image_url ? productImage(s.image_url) : null;
                        return (
                          <li key={s.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setOpen(false);
                                navigate(`/produkter/${s.slug || s.id}`);
                              }}
                              data-testid={`search-suggestion-${idx}`}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-stone-50 text-left transition-colors"
                            >
                              {imgSrc ? (
                                <img src={imgSrc} alt="" className="w-10 h-10 object-cover bg-stone-100 border border-stone-200 flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0">
                                  <Package className="w-4 h-4 text-stone-300" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-stone-900 truncate">{name}</p>
                                {s.matched_article && (
                                  <p className="text-xs font-mono text-emerald-700 truncate">
                                    {language === 'sv' ? 'Artikel' : 'Article'}: {s.matched_article}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductSearchButton;
